import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { RelatorioNucleoDto } from '@/api.types';
import { filtrarNucleosComEstagiarios } from './relatorio-nucleo.utils';
import { toast } from 'react-toastify';

// Estilos
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9,
    flexDirection: 'column',
    fontFamily: 'Helvetica',
  },
  title: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: 'bold',
  },
  updated: {
    fontSize: 9,
    textAlign: 'right',
    marginTop: 10,
    fontStyle: 'italic',
  },
  table: {
    flexDirection: 'column',
    width: '100%',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableRowOdd: {
    backgroundColor: '#f5f5f5',
  },
  tableRowEven: {
    backgroundColor: '#ffffff',
  },
  headerCell: {
    padding: 5,
    fontWeight: 'bold',
    backgroundColor: '#e0e6ed',
    textAlign: 'center',
  },
  tableCell: {
    padding: 4,
    textAlign: 'left',
  },
  lastCell: {
    // No border to remove
  },
  integrante: {
    marginBottom: 2,
  },
  nomeDiretor: {
    marginBottom: 2,
    fontWeight: 'bold',
  },
  inativo: {
    marginBottom: 2,
    textDecoration: 'line-through',
    color: 'gray',
  },
});

// Interface para a prop do componente PDF
interface RelatorioEstagiariosNucleosPDFProps {
  nucleos: RelatorioNucleoDto[];
}

// Componente PDF
function RelatorioEstagiariosNucleosPDF({ nucleos }: RelatorioEstagiariosNucleosPDFProps) {
  const today = new Date('2025-08-21'); // Data fixa baseada no contexto fornecido
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text style={styles.title}>ESTAGIÁRIOS POR NÚCLEO</Text>
        <Text style={styles.updated}>Atualizado em {formattedDate}</Text>

        <View style={styles.table}>
          {/* Cabeçalho */}
          <View style={[styles.tableRow, styles.headerCell]}>
            <Text style={[styles.headerCell, { flex: 1 }]}>NÚCLEOS</Text>
            <Text style={[styles.headerCell, { flex: 3 }]}>ESTAGIÁRIOS</Text>
          </View>

          {/* Corpo */}
          {nucleos.length > 0 ? (
            nucleos.map((nucleo, index) => (
              <View
                style={[
                  styles.tableRow,
                  index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                ]}
                key={index}
              >
                <Text style={[styles.tableCell, { flex: 1, textAlign: 'center' }]}>
                  {nucleo.nome || 'Nome não disponível'}
                  {"\n"}
                  ({nucleo.lotacoes?.length || 0} estagiários)
                </Text>
                <View style={[styles.tableCell, { flex: 3 }]}>
                  {nucleo.lotacoes && nucleo.lotacoes.length > 0 ? (
                    nucleo.lotacoes.map((lotacao, idx) => (
                      <Text
                        key={idx}
                        style={[
                          styles.integrante,
                          !lotacao.ativo ? styles.inativo : {},
                        ]}
                      >
                        {lotacao.estagio?.estagiario?.pessoa?.nome || 'Estagiário não disponível'}{' '}
                        {lotacao.anotacao1 ? `- ${lotacao.anotacao1}` : ''}{' '}
                        {lotacao.anotacao2 || ''}
                      </Text>
                    ))
                  ) : (
                    <Text style={styles.integrante}>Nenhum estagiário</Text>
                  )}
                </View>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 4, textAlign: 'center' }]}>
                Nenhum núcleo com estagiários disponível
              </Text>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
}

export async function generateEstagiariosNucleosPDF(): Promise<JSX.Element> {
  try {
    const { GetRelatorioNucleo } = await import('@/modules/reports/relatorio.service');
    const result = await GetRelatorioNucleo();
    return <RelatorioEstagiariosNucleosPDF nucleos={filtrarNucleosComEstagiarios(result)} />;
  } catch (err) {
    toast.error((err as string) || 'Erro ao buscar núcleos');
    return <RelatorioEstagiariosNucleosPDF nucleos={[]} />;
  }
}

export default RelatorioEstagiariosNucleosPDF;